import React, { useState, useEffect } from "react";
import FormulaDisplay from "./FormulaDisplay";
import "../App.css";

const kinds = [
  { label: "二氧化硫", value: "so2", limit: 2.5 },
  { label: "氮氧化物", value: "nox", limit: 2.5 },
  { label: "氧气", value: "o2", limit: 2.5 },
  { label: "颗粒物", value: "pm", limit: 2 },
  { label: "非甲烷总烃", value: "nmhc", limit: 3 },
];

const ranges = [
  { label: "0-25", value: "0-25" },
  { label: "0-30", value: "0-30" },
  { label: "0-100", value: "0-100" },
  { label: "0-120", value: "0-120" },
  { label: "自定义", value: "custom" },
];

const getDefaultRange = (kind) => {
  if (kind === "o2") return "0-25";
  if (kind === "pm") return "0-30";
  if (kind === "nmhc") return "0-120";
  return "0-100";
};

const getRangeInfo = (rangeStr) => {
  if (!rangeStr || !rangeStr.includes("-")) return { min: 0, max: 0, diff: 0 };
  const [min, max] = rangeStr.split("-").map(Number);
  return { min, max, diff: max - min };
};

const getKindLimit = (kind) => {
  const found = kinds.find(k => k.value === kind);
  return found ? found.limit : 2.5;
};

const getKindLabel = (kind) => {
  const found = kinds.find(k => k.value === kind);
  return found ? found.label : kind;
};

const CalibrationCalculator = () => {
  const [kind, setKind] = useState("so2");
  const [range, setRange] = useState(getDefaultRange("so2"));
  const [customRange, setCustomRange] = useState("");
  const [rangeInfo, setRangeInfo] = useState(getRangeInfo(getDefaultRange("so2")));

  // 零点漂移
  const [zeroBase, setZeroBase] = useState("");
  const [zeroLast, setZeroLast] = useState("");
  const [zeroCurrent, setZeroCurrent] = useState("");
  const [zeroDrift, setZeroDrift] = useState("");
  const [zeroStatus, setZeroStatus] = useState("");

  // 量程漂移
  const [spanBase, setSpanBase] = useState("");
  const [spanLast, setSpanLast] = useState("");
  const [spanCurrent, setSpanCurrent] = useState(""); // 校准前测试值
  const [spanDrift, setSpanDrift] = useState("");
  const [spanStatus, setSpanStatus] = useState("");

  // 监听种类变化，自动切换量程
  useEffect(() => {
    const defaultRange = getDefaultRange(kind);
    setRange(defaultRange);
    setCustomRange("");
    const info = getRangeInfo(defaultRange);
    setRangeInfo(info);
    setSpanBase(info.max === 0 ? "" : String(info.max));
    setSpanLast(info.max === 0 ? "" : String(info.max));
    setSpanCurrent(info.max === 0 ? "" : String(info.max));
  }, [kind]);

  // 监听量程变化
  useEffect(() => {
    let info;
    if (range === "custom") {
      info = getRangeInfo(customRange);
    } else {
      info = getRangeInfo(range);
    }
    setRangeInfo(info);
    setSpanBase(info.max === 0 ? "" : String(info.max));
    setSpanLast(info.max === 0 ? "" : String(info.max));
    setSpanCurrent(info.max === 0 ? "" : String(info.max));
  }, [range, customRange]);

  // 计算零点漂移
  useEffect(() => {
    const zl = zeroLast === "" ? 0 : Number(zeroLast);
    const zc = zeroCurrent === "" ? 0 : Number(zeroCurrent);
    if (zeroLast !== "" && zeroCurrent !== "" && rangeInfo.diff) {
      const drift = ((zc - zl) / rangeInfo.diff) * 100;
      setZeroDrift(drift.toFixed(2));
      const limit = getKindLimit(kind);
      const label = getKindLabel(kind);
      setZeroStatus(`${Math.abs(drift) <= limit ? "正常" : "不正常"}（${label}限值±${limit}%）`);
    } else {
      setZeroDrift("");
      setZeroStatus("");
    }
  }, [zeroLast, zeroCurrent, rangeInfo.diff, kind]);

  // 计算量程漂移
  useEffect(() => {
    const sl = spanLast === "" ? 0 : Number(spanLast);
    const sc = spanCurrent === "" ? 0 : Number(spanCurrent);
    if (spanLast !== "" && spanCurrent !== "" && rangeInfo.diff) {
      const drift = ((sc - sl) / rangeInfo.diff) * 100;
      setSpanDrift(drift.toFixed(2));
      const limit = getKindLimit(kind);
      const label = getKindLabel(kind);
      setSpanStatus(`${Math.abs(drift) <= limit ? "正常" : "不正常"}（${label}限值±${limit}%）`);
    } else {
      setSpanDrift("");
      setSpanStatus("");
    }
  }, [spanLast, spanCurrent, rangeInfo.diff, kind]);

  return (
    <div style={{ padding: 24, maxWidth: 700, margin: "0 auto" }}>
      <div style={{ display: "flex", gap: 24, marginBottom: 16, flexWrap: 'wrap' }}>
        <div>
          <label>种类：</label>
          <select value={kind} onChange={e => setKind(e.target.value)}>
            {kinds.map(k => (
              <option key={k.value} value={k.value}>{k.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label>分析仪量程：</label>
          <select value={range} onChange={e => setRange(e.target.value)}>
            {ranges.map(r => (
              <option key={r.value} value={r.value}>{r.label}</option>
            ))}
          </select>
          {range === "custom" && (
            <input
              type="text"
              placeholder="如 0-150"
              value={customRange}
              onChange={e => setCustomRange(e.target.value)}
              style={{ width: 90 }}
            />
          )}
        </div>
        <div>
          <label>计量单位：</label>
          <span>mg/m³</span>
        </div>
      </div>

      {/* 零点漂移校准 */}
      <h3>零点漂移校准</h3>
      <div className="calculator-section">
        <div>
          <label>零点浓度值</label>
          <input type="number" value={zeroBase} placeholder="0" onChange={e => setZeroBase(e.target.value)} />
        </div>
        <div>
          <label>上次校准后测试值</label>
          <input type="number" value={zeroLast} placeholder="0" onChange={e => setZeroLast(e.target.value)} />
        </div>
        <div>
          <label>校准前测试</label>
          <input type="number" value={zeroCurrent} placeholder="0" onChange={e => setZeroCurrent(e.target.value)} />
        </div>
        <div>
          <label>零点漂移%.F.S</label>
          <input type="text" value={zeroDrift} readOnly />
        </div>
        <div>
          <label>仪器校准是否正常</label>
          <input type="text" value={zeroStatus} readOnly />
        </div>
      </div>

      {/* 量程漂移校准 */}
      <h3>量程漂移校准</h3>
      <div className="calculator-section">
        <div>
          <label>标气浓度值</label>
          <input type="number" value={spanBase} placeholder="0" onChange={e => setSpanBase(e.target.value)} />
        </div>
        <div>
          <label>上次校准后测试值</label>
          <input type="number" value={spanLast} placeholder="0" onChange={e => setSpanLast(e.target.value)} />
        </div>
        <div>
          <label>校准前测试值</label>
          <input type="number" value={spanCurrent} placeholder="0" onChange={e => setSpanCurrent(e.target.value)} />
        </div>
        <div>
          <label>量程漂移%.F.S</label>
          <input type="text" value={spanDrift} readOnly />
        </div>
        <div>
          <label>仪器校准是否正常</label>
          <input type="text" value={spanStatus} readOnly />
        </div>
      </div>

      {/* 公式与计算过程展示 */}
      <FormulaDisplay title="公式与计算过程" defaultCollapsed={true}>
        <div>
          <b>零点漂移%.F.S：</b>
          <span>
            (校准前测试 - 上次校准后测试值) / 量程 × 100<br />
            = {zeroCurrent} - {zeroLast} / {rangeInfo.diff} × 100 = {zeroDrift}
          </span>
        </div>
        <div style={{ marginTop: 8 }}>
          <b>量程漂移%.F.S：</b>
          <span>
            (校准前测试值 - 上次校准后测试值) / 量程 × 100<br />
            = {spanCurrent} - {spanLast} / {rangeInfo.diff} × 100 = {spanDrift}
          </span>
        </div>
      </FormulaDisplay>
    </div>
  );
};

export default CalibrationCalculator; 